import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  AlertCircle,
  ArrowLeft,
  Award,
  CheckCircle2,
  ClipboardList,
  RotateCcw,
  XCircle,
} from 'lucide-react'
import { apiJson } from '../api'

interface LearnerQuestion {
  id: string
  orderIndex: number
  text: string
  options: string[]
  points: number
}

interface QuizLearnerView {
  id: string
  courseId: string
  title: string
  passScore: number
  questions: LearnerQuestion[]
  attemptCount: number
  bestScorePercent: number | null
  passed: boolean
}

interface AttemptResult {
  id: string
  attemptNumber: number
  earnedPoints: number
  totalPoints: number
  scorePercent: number
  passed: boolean
  perQuestion: { questionId: string; correct: boolean }[]
}

export default function QuizTake() {
  const { id: courseId } = useParams()
  const [quiz, setQuiz] = useState<QuizLearnerView | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [answers, setAnswers] = useState<Record<string, number>>({})
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<AttemptResult | null>(null)

  const load = useCallback(async () => {
    try {
      setQuiz(await apiJson<QuizLearnerView>(`/courses/${courseId}/quiz`))
      setError('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load quiz')
    } finally {
      setLoading(false)
    }
  }, [courseId])

  useEffect(() => {
    setLoading(true)
    load()
  }, [load])

  const answeredCount = useMemo(() => Object.keys(answers).length, [answers])
  const allAnswered = quiz ? answeredCount === quiz.questions.length : false
  const perQuestionMap = useMemo(
    () => new Map((result?.perQuestion ?? []).map((p) => [p.questionId, p.correct] as const)),
    [result],
  )

  const submit = async () => {
    if (!quiz) return
    setSubmitting(true)
    setError('')
    try {
      const payload = {
        answers: Object.entries(answers).map(([questionId, optionIndex]) => ({ questionId, optionIndex })),
      }
      const r = await apiJson<AttemptResult>(`/quizzes/${quiz.id}/attempts`, {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: { 'Content-Type': 'application/json' },
      })
      setResult(r)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submit failed')
    } finally {
      setSubmitting(false)
    }
  }

  const retry = () => {
    setAnswers({})
    setResult(null)
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600" />
      </div>
    )
  }

  if (!quiz) {
    return (
      <div className="space-y-4">
        <Link to={`/courses/${courseId}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 transition-colors hover:text-brand-600">
          <ArrowLeft size={15} /> Back to course
        </Link>
        <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle size={16} className="shrink-0" /> {error || 'Quiz not found'}
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <Link to={`/courses/${courseId}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 transition-colors hover:text-brand-600">
        <ArrowLeft size={15} /> Back to course
      </Link>

      {/* header */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center gap-3.5 border-b border-slate-100 px-6 py-5">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-md shadow-brand-500/25">
            <ClipboardList size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-bold tracking-tight">{quiz.title}</h1>
            <p className="text-xs text-slate-400">
              {quiz.questions.length} questions · pass at {quiz.passScore}%
              {quiz.attemptCount > 0 && ` · attempt ${quiz.attemptCount + 1}`}
              {quiz.bestScorePercent !== null && ` · best so far ${quiz.bestScorePercent}%`}
            </p>
          </div>
        </div>

        <div className="space-y-5 px-6 py-6">
          {error && (
            <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertCircle size={16} className="shrink-0" /> {error}
            </div>
          )}

          {/* result banner */}
          {result && (
            <div
              className={`flex items-start gap-3 rounded-2xl border p-5 ${
                result.passed ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'
              }`}
            >
              {result.passed ? (
                <CheckCircle2 size={22} className="mt-0.5 shrink-0 text-emerald-600" />
              ) : (
                <XCircle size={22} className="mt-0.5 shrink-0 text-red-500" />
              )}
              <div className="flex-1">
                <p className={`text-sm font-bold ${result.passed ? 'text-emerald-800' : 'text-red-800'}`}>
                  {result.passed ? 'You passed!' : 'Not quite — review and try again'}
                </p>
                <p className={`mt-0.5 text-xs ${result.passed ? 'text-emerald-600' : 'text-red-600'}`}>
                  Score {result.scorePercent}% ({result.earnedPoints}/{result.totalPoints} points) · pass mark {quiz.passScore}%
                  {result.passed && ' · course completed'}
                </p>
                <div className="mt-3 flex gap-2">
                  {!result.passed && (
                    <button
                      onClick={retry}
                      className="inline-flex items-center gap-1.5 rounded-xl bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
                    >
                      <RotateCcw size={12} /> Retake quiz
                    </button>
                  )}
                  <Link
                    to={`/courses/${courseId}`}
                    className={`inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-xs font-semibold text-white shadow-md ${
                      result.passed ? 'bg-gradient-to-r from-emerald-600 to-emerald-700 shadow-emerald-500/25' : 'bg-gradient-to-r from-brand-600 to-brand-700 shadow-brand-500/25'
                    }`}
                  >
                    {result.passed ? (
                      <>
                        <Award size={12} /> Back to course
                      </>
                    ) : (
                      'Back to course'
                    )}
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* questions */}
          {quiz.questions.map((q, qi) => {
            const verdict = result ? perQuestionMap.get(q.id) : undefined
            return (
              <fieldset
                key={q.id}
                className={`rounded-2xl border p-5 transition-colors ${
                  verdict === undefined
                    ? 'border-slate-200'
                    : verdict
                      ? 'border-emerald-200 bg-emerald-50/40'
                      : 'border-red-200 bg-red-50/40'
                }`}
              >
                <legend className="sr-only">Question {qi + 1}</legend>
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm font-semibold leading-relaxed">
                    <span className="mr-2 text-slate-400">{qi + 1}.</span>
                    {q.text}
                  </p>
                  <span className="shrink-0 text-[11px] font-medium text-slate-400">{q.points} pt{q.points === 1 ? '' : 's'}</span>
                </div>
                {verdict !== undefined && (
                  <p className={`mt-1.5 flex items-center gap-1 text-[11px] font-semibold ${verdict ? 'text-emerald-600' : 'text-red-500'}`}>
                    {verdict ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                    {verdict ? 'Correct' : 'Incorrect'}
                  </p>
                )}
                <div className="mt-3 space-y-2">
                  {q.options.map((opt, oi) => {
                    const selected = answers[q.id] === oi
                    return (
                      <label
                        key={oi}
                        className={`flex cursor-pointer items-center gap-3 rounded-xl border px-3.5 py-2.5 text-sm transition-all ${
                          result ? 'cursor-default' : ''
                        } ${
                          selected
                            ? 'border-brand-400 bg-brand-50 font-medium text-brand-800 ring-2 ring-brand-100'
                            : 'border-slate-200 bg-white text-slate-600 hover:border-brand-200 hover:bg-slate-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name={q.id}
                          checked={selected}
                          disabled={!!result}
                          onChange={() => setAnswers({ ...answers, [q.id]: oi })}
                          className="h-4 w-4 border-slate-300 text-brand-600 focus:ring-brand-400"
                        />
                        {opt}
                      </label>
                    )
                  })}
                </div>
              </fieldset>
            )
          })}

          {/* submit */}
          {!result && (
            <div className="flex items-center justify-between gap-3 pt-1">
              <p className="text-xs text-slate-400">
                {answeredCount}/{quiz.questions.length} answered
              </p>
              <button
                onClick={submit}
                disabled={submitting || !allAnswered}
                className="rounded-xl bg-gradient-to-r from-brand-600 to-brand-700 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand-500/25 transition-all hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {submitting ? 'Submitting…' : 'Submit answers'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
